/**
 * raisy Theme System
 * Centralized color and design system for raisy CRM
 * Built for Tailwind CSS + Shadcn UI
 */

// ============================================
// Core Brand Color Palette - RAISY
// ============================================

export const BRAND_COLORS = {
  // Primary Brand Colors
  teal: '#0AB3BF',              // Core brand identity - buttons, accents, primary CTA
  tealLight: '#4DD4E0',          // Lighter teal for hover states, backgrounds
  tealDark: '#087B8B',           // Darker teal for active states, contrast

  // Neutral Colors
  white: '#FFFFFF',
  black: '#000000',
  darkGray: '#1a1a1a',           // Dark theme backgrounds
  mediumGray: '#666666',         // Secondary text
  lightGray: '#f0f0f0',          // Light backgrounds
  softGray: '#F5F5F5',           // Light section backgrounds

  // Status Colors
  success: '#10B981',            // Green for success states
  successLight: '#D1FAE5',       // Light green background
  warning: '#F59E0B',            // Amber for warnings
  warningLight: '#FEF3C7',       // Light amber background
  error: '#EF4444',              // Red for errors
  errorLight: '#FEE2E2',         // Light red background
  info: '#3B82F6',               // Blue for info
  infoLight: '#DBEAFE',          // Light blue background

  // Additional Neutrals
  border: '#E5E7EB',             // Default border color
  borderLight: '#F3F4F6',        // Lighter borders
  muted: '#9CA3AF',              // Muted/disabled text

  // ============================================================
  // Opacity Variants - for backgrounds, borders, hover states
  // ============================================================

  // Teal opacity variants
  teal05: 'rgba(10, 179, 191, 0.05)',
  teal08: 'rgba(10, 179, 191, 0.08)',
  teal10: 'rgba(10, 179, 191, 0.1)',
  teal15: 'rgba(10, 179, 191, 0.15)',
  teal20: 'rgba(10, 179, 191, 0.2)',
  teal25: 'rgba(10, 179, 191, 0.25)',
  teal30: 'rgba(10, 179, 191, 0.3)',
  teal40: 'rgba(10, 179, 191, 0.4)',
  teal50: 'rgba(10, 179, 191, 0.5)',

  // Success opacity variants
  success08: 'rgba(16, 185, 129, 0.08)',
  success10: 'rgba(16, 185, 129, 0.1)',
  success15: 'rgba(16, 185, 129, 0.15)',
  success30: 'rgba(16, 185, 129, 0.3)',

  // Warning opacity variants
  warning08: 'rgba(245, 158, 11, 0.08)',
  warning10: 'rgba(245, 158, 11, 0.1)',
  warning15: 'rgba(245, 158, 11, 0.15)',
  warning30: 'rgba(245, 158, 11, 0.3)',

  // Error opacity variants
  error08: 'rgba(239, 68, 68, 0.08)',
  error10: 'rgba(239, 68, 68, 0.1)',
  error15: 'rgba(239, 68, 68, 0.15)',
  error30: 'rgba(239, 68, 68, 0.3)',

  // Info opacity variants
  info08: 'rgba(59, 130, 246, 0.08)',
  info10: 'rgba(59, 130, 246, 0.1)',
  info15: 'rgba(59, 130, 246, 0.15)',
  info30: 'rgba(59, 130, 246, 0.3)',

  // White opacity variants (for dark theme overlays)
  white05: 'rgba(255, 255, 255, 0.05)',
  white10: 'rgba(255, 255, 255, 0.1)',
  white20: 'rgba(255, 255, 255, 0.2)',
  white30: 'rgba(255, 255, 255, 0.3)',
  white40: 'rgba(255, 255, 255, 0.4)',
  white50: 'rgba(255, 255, 255, 0.5)',
  white60: 'rgba(255, 255, 255, 0.6)',

  // Black opacity variants (for light theme overlays)
  black05: 'rgba(0, 0, 0, 0.05)',
  black10: 'rgba(0, 0, 0, 0.1)',
  black20: 'rgba(0, 0, 0, 0.2)',
  black30: 'rgba(0, 0, 0, 0.3)',
} as const;

// ============================================
// Brand Gradients
// ============================================

export const BRAND_GRADIENTS = {
  primaryTeal: `linear-gradient(135deg, ${BRAND_COLORS.teal} 0%, ${BRAND_COLORS.tealLight} 100%)`,
  primaryTealDark: `linear-gradient(135deg, ${BRAND_COLORS.tealDark} 0%, ${BRAND_COLORS.teal} 100%)`,
  success: `linear-gradient(135deg, ${BRAND_COLORS.success} 0%, ${BRAND_COLORS.successLight} 100%)`,
  warning: `linear-gradient(135deg, ${BRAND_COLORS.warning} 0%, ${BRAND_COLORS.warningLight} 100%)`,
  error: `linear-gradient(135deg, ${BRAND_COLORS.error} 0%, ${BRAND_COLORS.errorLight} 100%)`,
} as const;

// ============================================
// Typography System
// ============================================

export const TYPOGRAPHY = {
  // Font families
  fontFamily: {
    sans: ['Inter', 'system-ui', 'sans-serif'],
    mono: ['Fira Code', 'monospace'],
  },

  // Font sizes (Tailwind-compatible)
  // NOTE: the canonical source of truth for runtime CSS is raisy_FE/src/index.css
  // (@theme inline). Keep these values in sync with the CSS tokens.
  fontSize: {
    '2xs': ['0.625rem', { lineHeight: '0.75rem' }], // 10px
    xs: ['0.75rem', { lineHeight: '1rem' }],         // 12px
    sm: ['0.875rem', { lineHeight: '1.25rem' }],     // 14px
    base: ['1rem', { lineHeight: '1.5rem' }],        // 16px
    md: ['0.9375rem', { lineHeight: '1.375rem' }],   // 15px
    lg: ['1.125rem', { lineHeight: '1.75rem' }],     // 18px
    xl: ['1.25rem', { lineHeight: '1.75rem' }],      // 20px
    '2xl': ['1.5rem', { lineHeight: '2rem' }],       // 24px
    '3xl': ['1.875rem', { lineHeight: '2.25rem' }],  // 30px
    '4xl': ['2.25rem', { lineHeight: '2.5rem' }],    // 36px
    '5xl': ['3rem', { lineHeight: '1.2' }],          // 48px
  },

  // Font weights
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
} as const;

// Pixel mirror of the CSS token scale for non-Tailwind consumers (Antd, MUI, charts).
// Keep in sync with raisy_FE/src/index.css @theme inline tokens.
export const RAISY_TYPOGRAPHY_PX = {
  fontFamily: "'Inter Variable', ui-sans-serif, system-ui, sans-serif",
  fontSize: 14,        // text-sm
  fontSizeSM: 12,      // text-label-sm
  fontSizeLG: 16,      // text-base
  fontSizeXL: 18,      // text-lg
  fontSizeHeading1: 30, // text-3xl
  fontSizeHeading2: 24, // text-2xl
  fontSizeHeading3: 20, // text-xl
  fontSizeHeading4: 18, // text-lg
  fontSizeHeading5: 16, // text-base
  lineHeight: 1.5,
} as const;

// ============================================
// Spacing System
// ============================================

export const SPACING = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  '2xl': '3rem',   // 48px
  '3xl': '4rem',   // 64px
  '4xl': '6rem',   // 96px
} as const;

// ============================================
// Shadow System
// ============================================

export const SHADOWS = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
  focus: `0 0 0 3px rgba(10, 179, 191, 0.1)`,  // Teal focus ring
} as const;

// ============================================
// Border Radius
// ============================================

export const BORDER_RADIUS = {
  none: '0',
  xs: '0.125rem',  // 2px
  sm: '0.25rem',   // 4px
  md: '0.5rem',    // 8px
  lg: '0.75rem',   // 12px
  xl: '1rem',      // 16px
  '2xl': '1.5rem', // 24px
  '3xl': '2rem',   // 32px
  full: '9999px',
} as const;

// ============================================
// Transitions/Animations
// ============================================

export const TRANSITIONS = {
  fast: '150ms ease-in-out',
  base: '250ms ease-in-out',
  slow: '350ms ease-in-out',
} as const;

// ============================================
// Helper: Get color with opacity
// ============================================

export const getColorWithOpacity = (hexColor: string, opacity: number): string => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// ============================================
// Theme Configuration (for Tailwind CSS)
// ============================================

export const tailwindThemeConfig = {
  colors: {
    // Brand Colors
    teal: {
      DEFAULT: BRAND_COLORS.teal,
      light: BRAND_COLORS.tealLight,
      dark: BRAND_COLORS.tealDark,
    },

    // Status Colors
    success: {
      DEFAULT: BRAND_COLORS.success,
      light: BRAND_COLORS.successLight,
    },
    warning: {
      DEFAULT: BRAND_COLORS.warning,
      light: BRAND_COLORS.warningLight,
    },
    error: {
      DEFAULT: BRAND_COLORS.error,
      light: BRAND_COLORS.errorLight,
    },
    info: {
      DEFAULT: BRAND_COLORS.info,
      light: BRAND_COLORS.infoLight,
    },

    // Neutrals
    white: BRAND_COLORS.white,
    black: BRAND_COLORS.black,
    gray: {
      light: BRAND_COLORS.lightGray,
      medium: BRAND_COLORS.mediumGray,
      dark: BRAND_COLORS.darkGray,
      soft: BRAND_COLORS.softGray,
    },
  },
  fontSize: TYPOGRAPHY.fontSize,
  fontWeight: TYPOGRAPHY.fontWeight,
  spacing: SPACING,
  borderRadius: BORDER_RADIUS,
  boxShadow: SHADOWS,
  transitionDuration: {
    fast: '150ms',
    base: '250ms',
    slow: '350ms',
  },
} as const;

// ============================================
// Diff Viewer Colors (GitHub-inspired)
// Ported from glowtest_admin_new UnifiedDiffViewer
// ============================================

export const DIFF_DARK = {
  addedBg: 'rgba(46, 160, 67, 0.15)',
  addedColor: '#3FB950',
  addedWordBg: 'rgba(46, 160, 67, 0.4)',
  addedGutterBg: 'rgba(46, 160, 67, 0.2)',
  removedBg: 'rgba(248, 81, 73, 0.15)',
  removedColor: '#F85149',
  removedWordBg: 'rgba(248, 81, 73, 0.4)',
  removedGutterBg: 'rgba(248, 81, 73, 0.2)',
  placeholderBg: 'rgba(255, 255, 255, 0.03)',
  viewerBg: '#0D1117',
  gutterBg: '#161B22',
  highlightBg: 'rgba(187, 128, 9, 0.15)',
  codeFoldBg: 'rgba(56, 139, 253, 0.1)',
  codeFoldColor: '#58A6FF',
} as const;

export const DIFF_LIGHT = {
  addedBg: 'rgba(46, 160, 67, 0.1)',
  addedColor: '#1A7F37',
  addedWordBg: 'rgba(46, 160, 67, 0.3)',
  addedGutterBg: 'rgba(46, 160, 67, 0.15)',
  removedBg: 'rgba(248, 81, 73, 0.1)',
  removedColor: '#CF222E',
  removedWordBg: 'rgba(248, 81, 73, 0.3)',
  removedGutterBg: 'rgba(248, 81, 73, 0.15)',
  placeholderBg: 'rgba(0, 0, 0, 0.02)',
  viewerBg: '#FFFFFF',
  gutterBg: '#F6F8FA',
  highlightBg: 'rgba(187, 128, 9, 0.1)',
  codeFoldBg: 'rgba(56, 139, 253, 0.08)',
  codeFoldColor: '#0969DA',
} as const;

export type DiffColors = typeof DIFF_DARK;

// ============================================
// Debug/Logging
// ============================================

console.log('[THEME_INIT_010] Raisy theme system initialized', {
  timestamp: new Date().toISOString(),
  primaryColor: BRAND_COLORS.teal,
  colorCount: Object.keys(BRAND_COLORS).length,
});

export default BRAND_COLORS;
